import { useCallback, useEffect, useMemo, useState } from 'react';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Activity, ArrowRight, Building2, Download, FileText, Globe, GitBranch, History, KeyRound, LayoutDashboard, LogOut, Menu, Megaphone, MessageCircle, MessagesSquare, PieChart, RefreshCw, ScrollText, Send, ShieldCheck, SlidersHorizontal, UserPlus, Users, Wallet, X } from 'lucide-react';
import api from '../../services/api';
import hubCommandNetwork from '../../assets/agent-hub/command-network.png';
import hubRelayMark from '../../assets/agent-hub/relay-mark.png';
import hubSecureRelay from '../../assets/agent-hub/secure-relay.png';
import { HUB_SECTION_ASSETS, HubAssetIcon } from './AgentHubIconKit';
import LeadCenterSection from './LeadCenterSection';
import './agents-hub.css';

const HUB_TOKEN_KEY = 'session_token';

const describeError = (err) => err?.response?.data?.detail
  || err?.response?.data?.message
  || err?.message
  || 'Something went wrong';

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const dayLabel = (day) => {
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const timeAgo = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never';
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const agentStatus = (agent) => {
  if (agent.disabled) return { cls: 'is-off', label: 'Disabled' };
  if (!agent.has_assignment || !agent.assignment_active) return { cls: 'is-idle', label: 'No scheme' };
  return { cls: 'is-live', label: 'Active' };
};

function HubTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#061c34', border: '1px solid #294763', borderRadius: 6, padding: '10px 12px', fontSize: 12, boxShadow: '0 12px 32px rgba(6, 28, 52, 0.2)' }}>
      <p style={{ margin: '0 0 4px', color: '#adbbca' }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: 0, color: entry.color || entry.stroke, fontWeight: 700 }}>
          {entry.name}: {money(entry.value)}
        </p>
      ))}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (next !== confirm) {
      setNotice({ kind: 'error', text: 'The new passwords do not match' });
      return;
    }
    if (next.length < 10) {
      setNotice({ kind: 'error', text: 'Use at least 10 characters — this login manages payouts' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await api.post('/api/auth/change-password', { current_password: current, new_password: next });
      setNotice({ kind: 'ok', text: 'Password changed. Use the new one next time you sign in.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setNotice({ kind: 'error', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hub-drawer-backdrop" onClick={onClose}>
      <aside className="hub-drawer" style={{ maxWidth: 420 }} aria-label="Change password" onClick={(e) => e.stopPropagation()}>
        <div className="hub-drawer-head">
          <div className="hub-drawer-id">
            <span className="hub-avatar"><KeyRound style={{ width: 15, height: 15 }} /></span>
            <div>
              <h2>Change password</h2>
              <p>Applies to the account you are signed in as.</p>
            </div>
          </div>
          <button type="button" className="hub-icon-button" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <form className="hub-drawer-body" onSubmit={submit}>
          {notice && (
            <p style={{
              margin: 0, padding: '9px 12px', borderRadius: 9, fontSize: 12,
              background: notice.kind === 'error' ? 'rgba(251, 113, 133, 0.1)' : 'rgba(52, 211, 153, 0.1)',
              border: `1px solid ${notice.kind === 'error' ? 'rgba(251, 113, 133, 0.35)' : 'rgba(52, 211, 153, 0.3)'}`,
              color: notice.kind === 'error' ? 'var(--hub-out)' : 'var(--hub-ok)',
            }}>{notice.text}</p>
          )}
          <div className="hub-form">
            <label>
              <span>Current password</span>
              <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
            </label>
            <label>
              <span>New password</span>
              <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
            </label>
            <label>
              <span>Confirm new password</span>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="hub-button" disabled={busy || !current || !next}>
                {busy ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}

function HubLogin({ onDone }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [site, setSite] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = { identifier, password, portal: 'admin' };
      // A master admin belongs to no tenant and leaves this blank; an
      // account created for one site can only be found with its slug.
      if (site.trim()) payload.tenant_slug = site.trim().toLowerCase();
      const res = await api.post('/api/auth/login', payload);
      const token = res.data?.token;
      if (!token) throw new Error('No session token returned');
      localStorage.setItem(HUB_TOKEN_KEY, token);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hub-login">
      <section className="hub-login-intro" aria-label="AgentHub control plane">
        <div className="hub-brand">
          <span className="hub-brand-mark"><img src={hubRelayMark} alt="" /></span>
          <div>
            <h1>AgentHub</h1>
            <p>Operations control plane</p>
          </div>
        </div>
        <div className="hub-login-copy">
          <span className="hub-eyebrow">One desk. Every network.</span>
          <h2>Direct the work.<br />See the whole story.</h2>
          <p>Agents, commissions, conversations, domains, and audit history stay connected without crowding the decision in front of you.</p>
        </div>
        <div className="hub-login-visual" aria-hidden="true">
          <img src={hubSecureRelay} alt="" />
        </div>
        <div className="hub-login-proof">
          <span><HubAssetIcon src={HUB_SECTION_ASSETS.domains} /> Tenant-scoped</span>
          <span><HubAssetIcon src={HUB_SECTION_ASSETS.ledger} /> Ledger-led</span>
          <span><HubAssetIcon src={HUB_SECTION_ASSETS.chat} /> Conversation ready</span>
        </div>
      </section>

      <form className="hub-login-card" onSubmit={submit}>
        <div className="hub-login-form-head">
          <span className="hub-eyebrow">Secure access</span>
          <h2>Open AgentHub</h2>
          <p>Use your master or site administration account.</p>
        </div>
        {error && <p className="hub-login-error">{error}</p>}
        <label>
          <span>Admin ID or email</span>
          <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" autoFocus />
        </label>
        <label>
          <span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </label>
        <label>
          <span>Site <em style={{ fontStyle: 'normal', textTransform: 'none', letterSpacing: 0, color: 'var(--hub-faint)' }}>— leave blank for master admin</em></span>
          <input value={site} onChange={(e) => setSite(e.target.value)} placeholder="slotsoceans" autoComplete="off" />
        </label>
        <button type="submit" disabled={busy}>
          <span>{busy ? 'Signing in…' : 'Enter AgentHub'}</span>
          {!busy && <ArrowRight />}
        </button>
        <p className="hub-login-security"><ShieldCheck /> Credentials stay within the secure admin session.</p>
      </form>
    </div>
  );
}

function FlowRow({ label, amount, kind = 'neutral' }) {
  const tone = kind === 'cut' ? 'var(--hub-out)'
    : kind === 'gain' ? 'var(--hub-in)'
      : kind === 'final' ? 'var(--hub-earn)' : 'var(--hub-text)';
  const sign = kind === 'cut' ? '−' : kind === 'gain' ? '+' : '';
  const framed = kind === 'total' || kind === 'final';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: framed ? '9px 12px' : '6px 12px', borderRadius: 10, fontSize: 12.5,
      border: framed ? '1px solid var(--hub-border)' : '1px solid transparent',
      background: kind === 'final' ? 'rgba(111, 159, 0, 0.08)' : framed ? 'rgba(32, 30, 26, 0.03)' : 'transparent',
    }}>
      <span style={{ color: kind === 'final' ? 'var(--hub-earn)' : 'var(--hub-text)', fontWeight: framed ? 700 : 500 }}>{label}</span>
      <strong className="hub-num" style={{ color: tone }}>{sign}{money(Math.abs(Number(amount || 0)))}</strong>
    </div>
  );
}

function Toggle({ on, onFlip }) {
  return <button type="button" className={`hub-switch ${on ? 'is-on' : ''}`} onClick={onFlip} aria-pressed={on} />;
}

function StatusPill({ value }) {
  const status = String(value || 'open').toLowerCase();
  return <span className={`hub-pill p-${status}`}>{status}</span>;
}

function NewAgentModal({ tenants, onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: '', email: '', username: '', password: '',
    tenant_id: tenants[0]?.id || '', commission_percent: '', commission_basis: 'profit',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        email: form.email.trim(), password: form.password,
        full_name: form.full_name.trim() || undefined,
        username: form.username.trim() || undefined,
        tenant_id: form.tenant_id || undefined,
        commission_basis: form.commission_basis || undefined,
      };
      if (form.commission_percent !== '') payload.commission_percent = Number(form.commission_percent);
      const res = await api.post('/api/admin/agent-earn/agents', payload);
      onCreated(res.data?.agent?.id);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Could not create the agent');
      setBusy(false);
    }
  };

  return (
    <div className="hub-modal-backdrop" onClick={onClose}>
      <form className="hub-modal hub-form" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div>
          <h3>New agent</h3>
          <p>Created ready to work: the selected tenant's share link is attached, chat is on, and portal login is live immediately.</p>
        </div>
        {error && <p className="hub-login-error">{error}</p>}
        <div className="hub-form-row">
          <label><span>Full name</span><input value={form.full_name} onChange={set('full_name')} /></label>
          <label><span>Username (optional)</span><input value={form.username} onChange={set('username')} /></label>
        </div>
        <label><span>Email</span><input type="email" required value={form.email} onChange={set('email')} /></label>
        <label><span>Password</span><input type="password" required minLength={6} value={form.password} onChange={set('password')} autoComplete="new-password" /></label>
        <div className="hub-form-row">
          <label>
            <span>Default tenant & share link</span>
            <select value={form.tenant_id} onChange={set('tenant_id')}>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name || tenant.slug}</option>)}
            </select>
          </label>
          <label>
            <span>Commission % (blank = default)</span>
            <input type="number" min="0" max="100" step="0.1" value={form.commission_percent} onChange={set('commission_percent')} />
          </label>
        </div>
        <label>
          <span>Basis</span>
          <select value={form.commission_basis} onChange={set('commission_basis')}>
            <option value="profit">Profit (fees applied)</option>
            <option value="net_revenue">Net revenue</option>
            <option value="in">IN only</option>
            <option value="out">OUT only</option>
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="hub-button is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="hub-button" disabled={busy}>{busy ? 'Creating…' : 'Create agent'}</button>
        </div>
      </form>
    </div>
  );
}

function AgentDrawer({ agentId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('overview');
  const [thread, setThread] = useState(null);       // {conversation, messages}
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [playerPromo, setPlayerPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({ code: '', fp_amount: '0', max_claims: '100', active: true });
  const [preview, setPreview] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tenantScopeId, setTenantScopeId] = useState('');

  useEffect(() => {
    if (tab !== 'preview' || preview) return;
    api.get(`/api/admin/agent-earn/agents/${agentId}/portal-preview`)
      .then((res) => setPreview(res.data))
      .catch(() => setPreview({ error: true }));
  }, [tab, preview, agentId]);

  const load = useCallback(async () => {
    const suffix = tenantScopeId ? `?tenant_id=${encodeURIComponent(tenantScopeId)}` : '';
    const res = await api.get(`/api/admin/agent-earn/agents/${agentId}/detail${suffix}`);
    setDetail(res.data);
    const promo = res.data?.player_promo || null;
    setPlayerPromo(promo);
    setPromoForm({
      code: promo?.code || '',
      fp_amount: String(promo?.fp_amount ?? 0),
      max_claims: String(promo?.max_claims || 100),
      active: promo?.active !== false,
    });
    setTenantScopeId((current) => current || res.data?.selected_tenant_id || '');
    const assignment = res.data?.assignment || {};
    setSettings({
      commission_percent: String(assignment.commission_percent ?? 10),
      commission_basis: assignment.commission_basis || 'net_revenue',
      active: assignment.active !== false,
      chat_enabled: assignment.chat_enabled !== false,
      agent_referral_unlocked: Boolean(assignment.agent_referral_unlocked),
    });
  }, [agentId, tenantScopeId]);

  useEffect(() => { load().catch(() => setDetail(null)); }, [load]);

  const openThread = async (conversation) => {
    const res = await api.get(`/api/admin/agent-earn/team-chat/${conversation.id}/messages`);
    setThread(res.data);
  };

  const sendReply = async () => {
    const message = reply.trim();
    if (!message || !thread?.conversation?.id || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/agent-earn/team-chat/${thread.conversation.id}/messages`, { message });
      setReply('');
      await openThread(thread.conversation);
    } finally {
      setBusy(false);
    }
  };

  const setThreadStatus = async (status) => {
    if (!thread?.conversation?.id) return;
    await api.patch(`/api/admin/agent-earn/team-chat/${thread.conversation.id}/status`, { status });
    await openThread(thread.conversation);
    await load();
  };

  const saveSettings = async () => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const assignment = detail.assignment || {};
      await api.post('/api/admin/agent-earn/assignments', {
        agent_id: agentId,
        tenant_id: tenantScopeId || detail.selected_tenant_id || detail.agent.tenant_id,
        commission_basis: settings.commission_basis,
        commission_percent: Number(settings.commission_percent || 0),
        include_referral_tree: assignment.include_referral_tree !== false,
        referral_depth: assignment.referral_depth || 0,
        active: settings.active,
        chat_enabled: settings.chat_enabled,
        agent_referral_unlocked: settings.agent_referral_unlocked,
        security_deposit_unlocked: Boolean(assignment.security_deposit_unlocked),
        investment_unlocked: Boolean(assignment.investment_unlocked),
        note: assignment.note || null,
      });
      await load();
      onChanged();
      setNotice({ kind: 'ok', text: 'Saved' });
    } catch (err) {
      setNotice({ kind: 'error', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const copyReferralLink = async () => {
    const link = detail?.assignment?.tracking_url;
    if (!link) {
      setNotice({ kind: 'error', text: 'Save the tenant link before copying it.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setNotice({ kind: 'ok', text: 'Tenant share link copied' });
    } catch {
      setNotice({ kind: 'error', text: 'Could not copy the link' });
    }
  };

  const savePlayerPromo = async () => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const tenantId = tenantScopeId || detail.selected_tenant_id || detail.agent?.tenant_id;
      const res = await api.put(
        `/api/admin/agent-earn/agents/${agentId}/promo?tenant_id=${encodeURIComponent(tenantId)}`,
        {
          code: promoForm.code || null,
          fp_amount: Number(promoForm.fp_amount || 0),
          max_claims: Number(promoForm.max_claims || 100),
          active: promoForm.active,
        },
      );
      setPlayerPromo(res.data?.promo || null);
      setPromoForm((current) => ({
        ...current,
        code: res.data?.promo?.code || current.code,
        fp_amount: String(res.data?.promo?.fp_amount ?? current.fp_amount),
        max_claims: String(res.data?.promo?.max_claims || current.max_claims),
        active: res.data?.promo?.active !== false,
      }));
      await load();
      setNotice({ kind: 'ok', text: 'Player promo saved' });
    } catch (err) {
      setNotice({ kind: 'error', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const toggleDisabled = async () => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      await api.patch(`/api/admin/agent-earn/agents/${agentId}/status`, { disabled: !detail.agent.disabled });
      await load();
      onChanged();
      setNotice({ kind: 'ok', text: 'Saved' });
    } catch (err) {
      setNotice({ kind: 'error', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const agent = detail?.agent;
  const snapshot = detail?.snapshot || {};
  const wallet = detail?.wallet || {};
  const playerSummary = detail?.player_summary || {};

  return (
    <>
      <div className="hub-drawer-backdrop" onClick={onClose} />
      <aside className="hub-drawer" aria-label="Agent detail">
        <div className="hub-drawer-head">
          <div className="hub-drawer-id">
            <span className="hub-avatar">{String(agent?.full_name || agent?.username || '?').slice(0, 1).toUpperCase()}</span>
            <div>
              <h2>{agent?.full_name || agent?.username || 'Loading…'}</h2>
              <p>{agent?.email} · {detail?.assignment?.tracking_code || 'no scheme'} · {detail?.rules?.commission_percent ?? '—'}%</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {agent && (
              <button type="button" className={`hub-button ${agent.disabled ? '' : 'is-danger'}`} onClick={toggleDisabled} disabled={busy}>
                {agent.disabled ? 'Enable' : 'Disable'}
              </button>
            )}
            <button type="button" className="hub-icon-button" onClick={onClose} aria-label="Close agent detail"><X /></button>
          </div>
        </div>
        {notice && (
          <p style={{
            margin: '0 18px', padding: '9px 12px', borderRadius: 9, fontSize: 12,
            background: notice.kind === 'error' ? 'rgba(251, 113, 133, 0.1)' : 'rgba(52, 211, 153, 0.1)',
            border: `1px solid ${notice.kind === 'error' ? 'rgba(251, 113, 133, 0.35)' : 'rgba(52, 211, 153, 0.3)'}`,
            color: notice.kind === 'error' ? 'var(--hub-out)' : 'var(--hub-ok)',
          }}>{notice.text}</p>
        )}
        <div className="hub-tabs">
          {[['overview', 'Overview'], ['money', 'Money'], ['chat', `Team Chat${detail?.team_conversations?.length ? ` (${detail.team_conversations.length})` : ''}`], ['preview', 'Their Portal'], ['settings', 'Settings']].map(([id, label]) => (
            <button key={id} type="button" className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        <div className="hub-drawer-body">
          {!detail ? <div className="hub-empty">Loading agent…</div> : (
            <>
              {tab === 'overview' && (
                <>
                  <div className="hub-stat-grid">
                    <div className="hub-stat"><small>Players</small><strong className="hub-num">{playerSummary.total || 0}</strong></div>
                    <div className="hub-stat tone-ok"><small>In profit</small><strong className="hub-num">{playerSummary.profit_players || 0} · {money(playerSummary.profit_usd)}</strong></div>
                    <div className="hub-stat tone-out"><small>In loss</small><strong className="hub-num">{playerSummary.loss_players || 0} · {money(playerSummary.loss_usd)}</strong></div>
                    <div className="hub-stat tone-in"><small>Money IN</small><strong className="hub-num">{money(snapshot.completed_deposits_usd)}</strong></div>
                    <div className="hub-stat tone-out"><small>Money OUT</small><strong className="hub-num">{money(snapshot.completed_cashouts_usd)}</strong></div>
                    <div className="hub-stat"><small>Net profit</small><strong className="hub-num">{money(snapshot.commissionable_profit_usd)}</strong></div>
                    <div className="hub-stat tone-earn"><small>Earned</small><strong className="hub-num">{money(wallet.lifetime_earned_usd)}</strong></div>
                    <div className="hub-stat"><small>Available</small><strong className="hub-num">{money(wallet.available_usd)}</strong></div>
                    <div className="hub-stat"><small>Locked</small><strong className="hub-num">{money(wallet.locked_usd)}</strong></div>
                    <div className="hub-stat"><small>Withdrawn</small><strong className="hub-num">{money(wallet.withdrawn_usd)}</strong></div>
                  </div>
                  <div>
                    <table className="hub-mini-table">
                      <thead>
                        <tr><th>Player</th><th>Source</th><th>Deposits</th><th>Cashouts</th><th>Net</th></tr>
                      </thead>
                      <tbody>
                        {(detail.players || []).length === 0 ? (
                          <tr><td colSpan={5}><div className="hub-empty">No attributed players yet.</div></td></tr>
                        ) : detail.players.map((player) => {
                          const net = Number(player.net_revenue_usd || 0);
                          return (
                            <tr key={player.player_id}>
                              <td>{player.full_name || player.username || player.player_id}</td>
                              <td className="hub-cell-muted">{player.source === 'referral_tree' ? 'tree' : 'direct'}</td>
                              <td className="hub-cell-in hub-num">{money(player.deposits_usd)}</td>
                              <td className="hub-cell-out hub-num">{money(player.withdrawals_usd)}</td>
                              <td className={`hub-num ${net < 0 ? 'hub-cell-out' : 'hub-cell-earn'}`}>{money(net)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {tab === 'money' && (
                <>
                  <div>
                    <h4 style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--hub-muted)' }}>Withdrawals</h4>
                    {(detail.withdrawals || []).length === 0 ? <div className="hub-empty">No withdrawal requests.</div>
                      : detail.withdrawals.map((row) => (
                        <div key={row.id} className="hub-ledger-row">
                          <div><b className="hub-num">{money(row.amount_usd)}</b> · {row.method}<small>{row.destination} · {timeAgo(row.created_at)}</small></div>
                          <StatusPill value={row.status} />
                        </div>
                      ))}
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--hub-muted)' }}>Recent ledger</h4>
                    {(detail.ledger || []).length === 0 ? <div className="hub-empty">No ledger entries.</div>
                      : detail.ledger.map((row) => (
                        <div key={row.id} className="hub-ledger-row">
                          <div>{row.reason}<small>{row.event_type || row.source_collection || ''} · {timeAgo(row.created_at)}</small></div>
                          <b className={`hub-num ${row.direction === 'DEBIT' ? 'hub-cell-out' : 'hub-cell-earn'}`}>
                            {row.direction === 'DEBIT' ? '−' : '+'}{money(row.amount_usd)}
                          </b>
                        </div>
                      ))}
                  </div>
                </>
              )}

              {tab === 'chat' && (
                thread ? (
                  <div className="hub-chat-thread">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div>
                        <b style={{ fontSize: 13 }}>{thread.conversation.team_label}</b>
                        <small style={{ display: 'block', color: 'var(--hub-muted)', fontSize: 11 }}>{thread.conversation.subject}</small>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <StatusPill value={thread.conversation.status} />
                        {thread.conversation.status !== 'resolved' && (
                          <button type="button" className="hub-button is-ghost" onClick={() => setThreadStatus('resolved')}>Resolve</button>
                        )}
                        <button type="button" className="hub-icon-button" onClick={() => setThread(null)} aria-label="Back to conversations"><X /></button>
                      </div>
                    </div>
                    {(thread.messages || []).map((msg) => (
                      <div key={msg.id} className={`hub-msg ${msg.sender_role === 'agent' ? 'from-agent' : 'from-admin'}`}>
                        {msg.body}
                        <small>{msg.sender_real_name || msg.sender_name} · {timeAgo(msg.created_at)}</small>
                      </div>
                    ))}
                    <div className="hub-chat-reply">
                      <input
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                        placeholder="Reply to the agent…"
                        aria-label="Reply message"
                      />
                      <button type="button" className="hub-button" onClick={sendReply} disabled={busy || !reply.trim()}><Send /></button>
                    </div>
                  </div>
                ) : (
                  <div className="hub-chat-list">
                    {(detail.team_conversations || []).length === 0 ? <div className="hub-empty">This agent has not messaged any team.</div>
                      : detail.team_conversations.map((conversation) => (
                        <button key={conversation.id} type="button" className="hub-chat-item" onClick={() => openThread(conversation)}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <b>{conversation.team_label} — {conversation.subject}</b>
                            <StatusPill value={conversation.status} />
                          </div>
                          <small>{conversation.last_message} · {timeAgo(conversation.updated_at)}</small>
                        </button>
                      ))}
                  </div>
                )
              )}

              {tab === 'preview' && (
                !preview ? <div className="hub-empty">Loading their portal…</div>
                  : preview.error ? <div className="hub-empty">Could not load this agent’s portal.</div>
                    : (() => {
                      const s = preview.summary || {};
                      const b = preview.analytics?.breakdown || {};
                      const w = preview.analytics?.wallet || {};
                      const fees = b.fee_percents || {};
                      return (
                        <>
                          <p style={{ margin: 0, padding: '9px 12px', borderRadius: 9, fontSize: 11.5,
                            background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.25)', color: 'var(--hub-in)' }}>
                            Exactly what {preview.agent?.public_identity || 'this agent'} sees in their own portal — same payload, read-only.
                          </p>
                          <div className="hub-stat-grid">
                            <div className="hub-stat tone-earn"><small>Their balance</small><strong className="hub-num">{money(s.balance_usd)}</strong></div>
                            <div className="hub-stat"><small>Available</small><strong className="hub-num">{money(w.available_usd)}</strong></div>
                            <div className="hub-stat"><small>Locked</small><strong className="hub-num">{money(w.locked_usd)}</strong></div>
                            <div className="hub-stat"><small>Customers</small><strong className="hub-num">{s.customers_total || 0}</strong></div>
                          </div>
                          <div className="hub-form" style={{ gap: 6 }}>
                            <FlowRow label="Customer deposits (IN)" amount={b.completed_deposits_usd} kind="gain" />
                            <FlowRow label="Customer cashouts (OUT)" amount={b.completed_cashouts_usd} kind="cut" />
                            <FlowRow label="Gross profit" amount={b.gross_profit_usd} kind="total" />
                            <FlowRow label={`IN fee ${fees.in ?? 0}%`} amount={b.payment_fee_usd} kind="cut" />
                            <FlowRow label={`OUT fee ${fees.out ?? 0}%`} amount={b.cashout_fee_usd} kind="cut" />
                            <FlowRow label={`Web fee ${fees.web ?? 0}%`} amount={b.web_fee_usd} kind="cut" />
                            <FlowRow label={`Net house ${fees.net_house ?? 0}% · ${Number(b.net_house_points || 0).toLocaleString()} pts`} amount={b.net_house_fee_usd} kind="cut" />
                            <FlowRow label="Net profit" amount={b.commissionable_profit_usd} kind="total" />
                            <FlowRow label={`They earn ${b.commission_percent ?? 0}%`} amount={b.calculated_agent_earnings_usd} kind="final" />
                          </div>
                          {(preview.assignments || []).map((assignment) => (
                            <div key={assignment.id} className="hub-stat" style={{ display: 'grid', gap: 4 }}>
                              <small>Their referral link</small>
                              <span style={{ fontSize: 11.5, color: 'var(--hub-text)', wordBreak: 'break-all' }}>{assignment.tracking_url}</span>
                              <span style={{ fontSize: 11, color: 'var(--hub-muted)' }}>Agent connection code {assignment.tracking_code}</span>
                            </div>
                          ))}
                        </>
                      );
                    })()
              )}

              {tab === 'settings' && settings && (
                <div className="hub-form">
                  <label>
                    <span>Tenant share link</span>
                    <select
                      value={tenantScopeId || detail.selected_tenant_id || ''}
                      onChange={(e) => {
                        setPreview(null);
                        setThread(null);
                        setDetail(null);
                        setTenantScopeId(e.target.value);
                      }}
                    >
                      {(detail.available_tenants || []).map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>{tenant.name || tenant.slug}</option>
                      ))}
                    </select>
                  </label>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--hub-muted)' }}>
                    Each tenant keeps its own share link and rules. Saving here adds or updates only the selected tenant; it never moves existing players or money.
                  </p>
                  <div className="hub-stat" style={{ display: 'grid', gap: 7 }}>
                    <small>{detail.assignment ? 'Active tenant referral link' : 'No link yet for this tenant'}</small>
                    {detail.assignment?.tracking_url ? (
                      <>
                        <span style={{ fontSize: 11.5, color: 'var(--hub-text)', wordBreak: 'break-all' }}>{detail.assignment.tracking_url}</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="hub-button is-ghost" onClick={copyReferralLink}>Copy share link</button>
                          <a className="hub-button is-ghost" href={detail.assignment.tracking_url} target="_blank" rel="noreferrer">Open</a>
                        </div>
                      </>
                    ) : <span style={{ fontSize: 11, color: 'var(--hub-muted)' }}>Save the settings to create this agent's unique referral link for the selected tenant.</span>}
                  </div>
                  <div className="hub-stat" style={{ display: 'grid', gap: 9 }}>
                    <div>
                      <small>Player promo code — FP reward only</small>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--hub-muted)' }}>
                        This is separate from the agent connection link/code. Redeeming it gives the player FP and never changes their referred agent.
                      </p>
                    </div>
                    {detail.assignment ? (
                      <>
                        <div className="hub-form-row">
                          <label>
                            <span>Promo code</span>
                            <input value={promoForm.code} maxLength={32} placeholder="PROMO-PLAYER" onChange={(e) => setPromoForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))} />
                          </label>
                          <label>
                            <span>Player FP bonus</span>
                            <input type="number" min="0" step="0.01" value={promoForm.fp_amount} onChange={(e) => setPromoForm((prev) => ({ ...prev, fp_amount: e.target.value }))} />
                          </label>
                          <label>
                            <span>Max redemptions</span>
                            <input type="number" min="1" step="1" value={promoForm.max_claims} onChange={(e) => setPromoForm((prev) => ({ ...prev, max_claims: e.target.value }))} />
                          </label>
                        </div>
                        <div className="hub-toggle">
                          <div><b>Promo active</b><small>{playerPromo?.claim_count || 0} redemption{Number(playerPromo?.claim_count || 0) === 1 ? '' : 's'} used so far.</small></div>
                          <Toggle on={promoForm.active} onFlip={() => setPromoForm((prev) => ({ ...prev, active: !prev.active }))} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button type="button" className="hub-button is-ghost" onClick={savePlayerPromo} disabled={busy}>
                            {busy ? 'Saving…' : playerPromo ? 'Save player promo' : 'Create player promo'}
                          </button>
                        </div>
                      </>
                    ) : <span style={{ fontSize: 11, color: 'var(--hub-muted)' }}>Create the tenant link first, then set this agent’s player promo.</span>}
                  </div>
                  <div className="hub-form-row">
                    <label>
                      <span>Commission %</span>
                      <input type="number" min="0" max="100" step="0.1" value={settings.commission_percent}
                        onChange={(e) => setSettings((prev) => ({ ...prev, commission_percent: e.target.value }))} />
                    </label>
                    <label>
                      <span>Basis</span>
                      <select value={settings.commission_basis}
                        onChange={(e) => setSettings((prev) => ({ ...prev, commission_basis: e.target.value }))}>
                        <option value="profit">Profit (fees applied)</option>
                        <option value="net_revenue">Net revenue</option>
                        <option value="in">IN only</option>
                        <option value="out">OUT only</option>
                      </select>
                    </label>
                  </div>
                  {detail.rules?.configured && (
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--hub-muted)' }}>
                      Tenant fee rules are live (IN {detail.rules.in_fee_percent}% · OUT {detail.rules.out_fee_percent}% · web {detail.rules.web_fee_percent}% · house {detail.rules.net_house_fee_percent}%), so the profit basis applies them automatically.
                    </p>
                  )}
                  <div className="hub-toggle">
                    <div><b>Scheme active</b><small>Off stops attribution and commission for new activity.</small></div>
                    <Toggle on={settings.active} onFlip={() => setSettings((prev) => ({ ...prev, active: !prev.active }))} />
                  </div>
                  <div className="hub-toggle">
                    <div><b>Customer chat</b><small>Routes attributed players' chats to this agent.</small></div>
                    <Toggle on={settings.chat_enabled} onFlip={() => setSettings((prev) => ({ ...prev, chat_enabled: !prev.chat_enabled }))} />
                  </div>
                  <div className="hub-toggle">
                    <div><b>Agent referrals</b><small>Lets this agent recruit other agents with their code.</small></div>
                    <Toggle on={settings.agent_referral_unlocked} onFlip={() => setSettings((prev) => ({ ...prev, agent_referral_unlocked: !prev.agent_referral_unlocked }))} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="button" className="hub-button" onClick={saveSettings} disabled={busy}>
                      {busy ? 'Saving…' : detail?.assignment ? 'Save settings' : 'Assign scheme'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function ChatOversightSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/api/admin/agent-earn/chat-oversight')
      .then((res) => setData(res.data))
      .catch(() => setData({ rows: [], totals: {} }));
  }, []);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  return (
    <>
      <div className="hub-stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="hub-stat"><small>Conversations</small><strong className="hub-num">{totals.conversations || 0}</strong></div>
        <div className="hub-stat tone-out"><small>Unanswered</small><strong className="hub-num">{totals.unanswered || 0}</strong></div>
        <div className="hub-stat tone-out"><small>Waiting over a day</small><strong className="hub-num">{totals.waiting_over_day || 0}</strong></div>
        <div className="hub-stat"><small>Chat off</small><strong className="hub-num">{totals.no_chat_agents || 0}</strong></div>
      </div>
      <section className="hub-panel">
        <div className="hub-panel-head">
          <div>
            <h2>Who is keeping up</h2>
            <p>Answering happens in the chat screen — this is the queue, so a customer waiting on a quiet agent is never invisible.</p>
          </div>
        </div>
        {data === null ? <div className="hub-empty">Reading the inboxes…</div>
          : rows.length === 0 ? <div className="hub-empty">No agents with customer chats.</div>
            : (
              <div className="hub-table-wrap">
                <table className="hub-table" style={{ minWidth: 640 }}>
                  <thead>
                    <tr><th>Agent</th><th>Chat</th><th>Conversations</th><th>Unanswered</th><th>Over a day</th><th>Last message</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.agent_id}>
                        <td>
                          <div className="hub-agent-cell">
                            <span className="hub-avatar">{String(row.name || '?').slice(0, 1).toUpperCase()}</span>
                            <div><b>{row.name}</b>{row.disabled && <small>disabled</small>}</div>
                          </div>
                        </td>
                        <td>
                          <span className={`hub-status ${row.chat_enabled ? 'is-live' : 'is-off'}`}>
                            <i />{row.chat_enabled ? 'On' : 'Off'}
                          </span>
                        </td>
                        <td className="hub-num">{row.conversations}</td>
                        <td className={`hub-num ${row.unanswered ? 'hub-cell-out' : 'hub-cell-muted'}`}>{row.unanswered}</td>
                        <td className={`hub-num ${row.waiting_over_day ? 'hub-cell-out' : 'hub-cell-muted'}`}>{row.waiting_over_day}</td>
                        <td className="hub-cell-muted">{row.last_message_at ? timeAgo(row.last_message_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>
    </>
  );
}

function DomainsSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get('/api/admin/agent-earn/domains')
      .then((res) => setRows(res.data?.rows || []))
      .catch(() => setRows([]));
  }, []);

  const STATUS_CLASS = { active: 'is-live', pending_dns: 'is-idle', disabled: 'is-off', shared: 'is-idle' };
  return (
    <section className="hub-panel">
      <div className="hub-panel-head">
        <div>
          <h2>Agent portal domains</h2>
          <p>Where agents sign in. A domain that is not active stops new sign-ins and link resolution without touching historic data.</p>
        </div>
      </div>
      {rows === null ? <div className="hub-empty">Reading domain state…</div>
        : rows.length === 0 ? <div className="hub-empty">No tenants in scope.</div>
          : (
            <div className="hub-table-wrap">
              <table className="hub-table" style={{ minWidth: 680 }}>
                <thead>
                  <tr><th>Tenant</th><th>Status</th><th>Primary domain</th><th>All domains</th><th>Verified</th><th>Agents</th></tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.tenant_id}>
                      <td><b>{row.tenant_name}</b></td>
                      <td>
                        <span className={`hub-status ${STATUS_CLASS[row.status] || 'is-idle'}`}>
                          <i />{row.status}
                        </span>
                      </td>
                      <td className="hub-cell-muted">{row.primary || '—'}</td>
                      <td className="hub-cell-muted">{row.domains?.length ? row.domains.join(', ') : '—'}</td>
                      <td className="hub-cell-muted">{row.verified_at ? timeAgo(row.verified_at) : 'never'}</td>
                      <td className="hub-num">{row.agents}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--hub-muted)' }}>
        Read-only here on purpose: enabling a domain requires a DNS verification the master control plane owns, and a status change is the one edit worth making where that check lives.
      </p>
    </section>
  );
}

function AuditSection() {
  const [data, setData] = useState(null);
  const [action, setAction] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    const params = new URLSearchParams({ days: String(days), limit: '200' });
    if (action) params.set('action', action);
    api.get(`/api/admin/agent-earn/audit?${params.toString()}`)
      .then((res) => setData(res.data))
      .catch(() => setData({ rows: [], actions: [] }));
  }, [action, days]);

  const rows = data?.rows || [];
  const describe = (row) => {
    if (row.detail) return row.detail;
    const before = row.before || {};
    const after = row.after || {};
    const changed = Object.keys(after).filter((key) => String(before[key] ?? '') !== String(after[key] ?? ''));
    if (!changed.length) return '—';
    return changed.map((key) => `${key}: ${before[key] ?? '—'} → ${after[key]}`).join(' · ');
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="hub-search" style={{ width: 200 }} value={action} onChange={(e) => setAction(e.target.value)} aria-label="Filter by action">
          <option value="">All actions</option>
          {(data?.actions || []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <div className="hub-filter-chips">
          {[[7, '7d'], [30, '30d'], [90, '90d'], [0, 'All time']].map(([value, label]) => (
            <button key={label} type="button" className={days === value ? 'is-active' : ''} onClick={() => setDays(value)}>{label}</button>
          ))}
        </div>
      </div>
      <section className="hub-panel">
        {data === null ? <div className="hub-empty">Reading the trail…</div>
          : rows.length === 0 ? <div className="hub-empty">Nothing recorded in this window. Only changes made from now on appear here.</div>
            : (
              <div className="hub-table-wrap">
                <table className="hub-table" style={{ minWidth: 720 }}>
                  <thead>
                    <tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Change</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="hub-cell-muted">{timeAgo(row.created_at)}</td>
                        <td>{row.actor_name}</td>
                        <td className="hub-cell-earn">{row.action}</td>
                        <td className="hub-cell-muted">{row.target_name || row.target_id || '—'}</td>
                        <td className="hub-cell-muted" style={{ whiteSpace: 'normal', maxWidth: 320 }}>{describe(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>
    </>
  );
}

function BroadcastSection({ agentCount, onSent }) {
  const [form, setForm] = useState({ team: 'communication', subject: '', message: '', audience: 'active' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const AUDIENCES = [
    ['active', 'Active agents'],
    ['with_scheme', 'On a scheme'],
    ['no_scheme', 'No scheme yet'],
    ['all', 'Everyone (incl. disabled)'],
  ];

  const send = async () => {
    if (busy || !form.subject.trim() || !form.message.trim()) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/api/admin/agent-earn/broadcast', {
        team: form.team, subject: form.subject.trim(), message: form.message.trim(), audience: form.audience,
      });
      setResult(res.data);
      setForm((prev) => ({ ...prev, subject: '', message: '' }));
      onSent();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the broadcast');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hub-panel" style={{ maxWidth: 600 }}>
      <div className="hub-panel-head">
        <div>
          <h2>Broadcast to agents</h2>
          <p>Each agent receives it as their own team conversation, so a reply comes back as a private thread — not a group they can all see.</p>
        </div>
      </div>
      <div className="hub-form">
        {error && <p className="hub-login-error">{error}</p>}
        {result && (
          <p style={{ margin: 0, padding: '9px 12px', borderRadius: 9, fontSize: 12,
            background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', color: 'var(--hub-ok)' }}>
            Sent to {result.sent} agent{result.sent === 1 ? '' : 's'}{result.skipped ? ` · ${result.skipped} skipped` : ''}.
          </p>
        )}
        <div className="hub-form-row">
          <label>
            <span>From team</span>
            <select value={form.team} onChange={set('team')}>
              <option value="communication">Communication Team</option>
              <option value="customer_support">Customer Support Team</option>
              <option value="confirmation">Confirmation Team</option>
              <option value="managers">Managers</option>
              <option value="development">Development Team</option>
            </select>
          </label>
          <label>
            <span>Audience</span>
            <select value={form.audience} onChange={set('audience')}>
              {AUDIENCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        </div>
        <label><span>Subject</span><input value={form.subject} onChange={set('subject')} placeholder="Weekend promo is live" /></label>
        <label>
          <span>Message</span>
          <textarea
            value={form.message}
            onChange={set('message')}
            rows={5}
            placeholder="What should every agent know?"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--hub-border)',
              background: 'rgba(5, 7, 13, 0.65)', color: 'var(--hub-text)', fontSize: 12.5, outline: 'none',
              resize: 'vertical', fontFamily: 'inherit' }}
          />
        </label>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <small style={{ color: 'var(--hub-muted)', fontSize: 11 }}>{agentCount} agent{agentCount === 1 ? '' : 's'} in the roster</small>
          <button type="button" className="hub-button" onClick={send} disabled={busy || !form.subject.trim() || !form.message.trim()}>
            <Megaphone /> {busy ? 'Sending…' : 'Send broadcast'}
          </button>
        </div>
      </div>
    </section>
  );
}

function ReferralNetworkSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/api/admin/agent-earn/referral-network')
      .then((res) => setData(res.data))
      .catch(() => setData({ nodes: [], totals: {} }));
  }, []);

  const totals = data?.totals || {};
  const nodes = data?.nodes || [];
  return (
    <>
      <div className="hub-stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="hub-stat"><small>Recruiters</small><strong className="hub-num">{totals.recruiters || 0}</strong></div>
        <div className="hub-stat"><small>Recruited agents</small><strong className="hub-num">{totals.recruited_agents || 0}</strong></div>
        <div className="hub-stat tone-ok"><small>Referrals unlocked</small><strong className="hub-num">{totals.unlocked || 0}</strong></div>
        <div className="hub-stat tone-earn"><small>Override paid</small><strong className="hub-num">{money(totals.override_paid_usd)}</strong></div>
      </div>
      <section className="hub-panel">
        {data === null ? <div className="hub-empty">Loading the network…</div>
          : nodes.length === 0 ? <div className="hub-empty">No agents yet.</div>
            : (
              <div className="hub-table-wrap">
                <table className="hub-table" style={{ minWidth: 720 }}>
                  <thead>
                    <tr><th>Agent</th><th>Recruited by</th><th>Can recruit</th><th>Their code</th><th>Recruits</th><th>Override %</th><th>Override earned</th></tr>
                  </thead>
                  <tbody>
                    {nodes.map((node) => (
                      <tr key={node.id}>
                        <td>
                          <div className="hub-agent-cell">
                            <span className="hub-avatar">{String(node.name || '?').slice(0, 1).toUpperCase()}</span>
                            <div><b>{node.name}</b>{node.disabled && <small>disabled</small>}</div>
                          </div>
                        </td>
                        <td className="hub-cell-muted">{node.parent_name || '—'}</td>
                        <td>
                          <span className={`hub-status ${node.unlocked ? 'is-live' : 'is-idle'}`}>
                            <i />{node.unlocked ? 'Unlocked' : 'Locked'}
                          </span>
                        </td>
                        <td className="hub-cell-muted">{node.referral_code || '—'}</td>
                        <td className="hub-num">{node.recruits}</td>
                        <td className="hub-cell-muted hub-num">{node.override_percent}%</td>
                        <td className="hub-cell-earn hub-num">{money(node.override_earned_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>
    </>
  );
}

function StatementsSection() {
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const query = month ? `?month=${month}` : '';
    api.get(`/api/admin/agent-earn/statements${query}`)
      .then((res) => { setData(res.data); if (!month) setMonth(res.data.month); })
      .catch(() => setData({ statements: [], totals: {}, months: [] }));
  }, [month]);

  const totals = data?.totals || {};
  const statements = data?.statements || [];

  const exportCsv = () => {
    const header = ['month', 'agent', 'email', 'earned_usd', 'clawback_usd', 'net_usd', 'withdrawn_usd', 'events'];
    const lines = statements.map((row) => [
      data.month, row.name, row.email || '', row.earned_usd, row.clawback_usd, row.net_usd, row.withdrawn_usd, row.events,
    ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `agent-statements-${data.month}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="hub-search" style={{ width: 150 }} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Statement month">
          {(data?.months || []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <button type="button" className="hub-button is-ghost" style={{ marginLeft: 'auto' }} onClick={exportCsv} disabled={!statements.length}>
          <Download /> CSV
        </button>
      </div>
      <div className="hub-stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="hub-stat"><small>Agents paid</small><strong className="hub-num">{totals.agents || 0}</strong></div>
        <div className="hub-stat tone-earn"><small>Earned</small><strong className="hub-num">{money(totals.earned_usd)}</strong></div>
        <div className="hub-stat tone-out"><small>Clawbacks</small><strong className="hub-num">{money(totals.clawback_usd)}</strong></div>
        <div className="hub-stat"><small>Net owed</small><strong className="hub-num">{money(totals.net_usd)}</strong></div>
        <div className="hub-stat"><small>Withdrawn</small><strong className="hub-num">{money(totals.withdrawn_usd)}</strong></div>
      </div>
      <section className="hub-panel">
        {data === null ? <div className="hub-empty">Building statements…</div>
          : statements.length === 0 ? <div className="hub-empty">No commission activity in {data.month}.</div>
            : (
              <div className="hub-table-wrap">
                <table className="hub-table" style={{ minWidth: 680 }}>
                  <thead>
                    <tr><th>Agent</th><th>Events</th><th>Earned</th><th>Clawbacks</th><th>Net</th><th>Withdrawn</th></tr>
                  </thead>
                  <tbody>
                    {statements.map((row) => (
                      <tr key={row.agent_id}>
                        <td>
                          <div className="hub-agent-cell">
                            <span className="hub-avatar">{String(row.name || '?').slice(0, 1).toUpperCase()}</span>
                            <div><b>{row.name}</b><small>{row.email}</small></div>
                          </div>
                        </td>
                        <td className="hub-cell-muted hub-num">{row.events}</td>
                        <td className="hub-cell-earn hub-num">{money(row.earned_usd)}</td>
                        <td className="hub-cell-out hub-num">{money(row.clawback_usd)}</td>
                        <td className="hub-num">{money(row.net_usd)}</td>
                        <td className="hub-cell-muted hub-num">{money(row.withdrawn_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>
    </>
  );
}

function LedgerSection() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ agent_id: '', reason: '', direction: '', days: 30 });
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (skip) => {
    const params = new URLSearchParams({ days: String(filters.days), limit: '100', skip: String(skip) });
    if (filters.agent_id) params.set('agent_id', filters.agent_id);
    if (filters.reason) params.set('reason', filters.reason);
    if (filters.direction) params.set('direction', filters.direction);
    const res = await api.get(`/api/admin/agent-earn/ledger?${params.toString()}`);
    return res.data;
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await fetchPage(0);
      if (cancelled) return;
      setData(page);
      setRows(page.rows || []);
    })().catch(() => { setData({ totals: {}, reasons: [], agents: [], has_more: false }); setRows([]); });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await fetchPage(rows.length);
      setData((prev) => ({ ...page, reasons: prev?.reasons?.length ? prev.reasons : page.reasons }));
      setRows((prev) => [...prev, ...(page.rows || [])]);
    } finally {
      setLoadingMore(false);
    }
  };

  const set = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const exportCsv = () => {
    const header = ['date', 'agent', 'reason', 'event', 'player', 'direction', 'amount_usd', 'basis_usd', 'percent'];
    const lines = rows.map((row) => [
      row.created_at, row.agent_name || row.agent_id, row.reason, row.event_type || '',
      row.player_name || '', row.direction, row.amount_usd,
      (row.basis_amount_usd ?? '') === '' ? '' : (row.basis_amount_usd * (row.basis_sign || 1)),
      row.commission_percent ?? '',
    ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `agent-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const totals = data?.totals || {};
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select className="hub-search" style={{ width: 170 }} value={filters.agent_id} onChange={set('agent_id')} aria-label="Filter by agent">
          <option value="">All agents</option>
          {(data?.agents || []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
        </select>
        <select className="hub-search" style={{ width: 200 }} value={filters.reason} onChange={set('reason')} aria-label="Filter by reason">
          <option value="">All reasons</option>
          {(data?.reasons || []).map((reason) => <option key={reason} value={reason}>{reason}</option>)}
        </select>
        <div className="hub-filter-chips">
          {[['', 'All'], ['CREDIT', 'Credits'], ['DEBIT', 'Debits']].map(([value, label]) => (
            <button key={label} type="button" className={filters.direction === value ? 'is-active' : ''}
              onClick={() => setFilters((prev) => ({ ...prev, direction: value }))}>{label}</button>
          ))}
        </div>
        <div className="hub-filter-chips">
          {[[7, '7d'], [30, '30d'], [90, '90d'], [0, 'All time']].map(([value, label]) => (
            <button key={label} type="button" className={filters.days === value ? 'is-active' : ''}
              onClick={() => setFilters((prev) => ({ ...prev, days: value }))}>{label}</button>
          ))}
        </div>
        <button type="button" className="hub-button is-ghost" style={{ marginLeft: 'auto' }} onClick={exportCsv} disabled={!rows.length}>
          <Download /> CSV
        </button>
      </div>

      <div className="hub-stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="hub-stat tone-earn"><small>Credits</small><strong className="hub-num">{money(totals.credits_usd)}</strong></div>
        <div className="hub-stat tone-out"><small>Debits</small><strong className="hub-num">{money(totals.debits_usd)}</strong></div>
        <div className="hub-stat"><small>Net (this filter)</small><strong className="hub-num">{money(totals.net_usd)}</strong></div>
      </div>

      <section className="hub-panel">
        {data === null ? <div className="hub-empty">Loading the money trail…</div>
          : rows.length === 0 ? <div className="hub-empty">No ledger events match this filter.</div>
            : (
              <>
                <div className="hub-table-wrap">
                  <table className="hub-table" style={{ minWidth: 760 }}>
                    <thead>
                      <tr><th>When</th><th>Agent</th><th>Reason</th><th>Player</th><th>Base × %</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td className="hub-cell-muted">{timeAgo(row.created_at)}</td>
                          <td>{row.agent_name || '—'}</td>
                          <td>
                            {row.reason}
                            {row.event_type && <small style={{ display: 'block', color: 'var(--hub-faint)', fontSize: 9.5 }}>{row.event_type}</small>}
                          </td>
                          <td className="hub-cell-muted">{row.player_name || '—'}</td>
                          <td className="hub-cell-muted hub-num">
                            {row.basis_amount_usd != null ? `${money(row.basis_amount_usd * (row.basis_sign || 1))} × ${row.commission_percent ?? '—'}%` : '—'}
                          </td>
                          <td className={`hub-num ${row.direction === 'DEBIT' ? 'hub-cell-out' : 'hub-cell-earn'}`}>
                            {row.direction === 'DEBIT' ? '−' : '+'}{money(row.amount_usd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.has_more && (
                  <div style={{ display: 'grid', placeItems: 'center', paddingTop: 12 }}>
                    <button type="button" className="hub-button is-ghost" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
      </section>
    </>
  );
}

function WithdrawalsSection() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(`/api/admin/agent-earn/withdrawals${filter === 'all' ? '' : `?status=${filter}`}`);
    setRows(res.data?.withdrawals || []);
  }, [filter]);
  useEffect(() => { load().catch(() => setRows([])); }, [load]);

  const decide = async (row, action) => {
    if (busyId) return;
    setBusyId(row.id);
    try {
      await api.post(`/api/admin/agent-earn/withdrawals/${row.id}/${action}`, { admin_note: null });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="hub-filter-chips">
        {['pending', 'approved', 'rejected', 'all'].map((value) => (
          <button key={value} type="button" className={filter === value ? 'is-active' : ''} onClick={() => setFilter(value)}>
            {value[0].toUpperCase() + value.slice(1)}
          </button>
        ))}
      </div>
      <section className="hub-panel">
        {rows === null ? <div className="hub-empty">Loading withdrawals…</div>
          : rows.length === 0 ? <div className="hub-empty">No {filter === 'all' ? '' : filter + ' '}withdrawals.</div>
            : rows.map((row) => (
              <div key={row.id} className="hub-ledger-row">
                <div>
                  <b className="hub-num">{money(row.amount_usd)}</b> · {row.agent_name || row.agent_username}
                  <small>{row.method} → {row.destination} · {timeAgo(row.created_at)}{row.note ? ` · “${row.note}”` : ''}</small>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <StatusPill value={row.status} />
                  {row.status === 'pending' && (
                    <>
                      <button type="button" className="hub-button" onClick={() => decide(row, 'approve')} disabled={busyId === row.id}>Approve</button>
                      <button type="button" className="hub-button is-danger" onClick={() => decide(row, 'reject')} disabled={busyId === row.id}>Reject</button>
                    </>
                  )}
                </div>
              </div>
            ))}
      </section>
    </>
  );
}

function TeamChatSection() {
  const [conversations, setConversations] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get('/api/admin/agent-earn/team-chat');
    setConversations(res.data?.conversations || []);
  }, []);
  useEffect(() => { load().catch(() => setConversations([])); }, [load]);

  const openThread = async (conversation) => {
    const res = await api.get(`/api/admin/agent-earn/team-chat/${conversation.id}/messages`);
    setThread(res.data);
  };

  const sendReply = async () => {
    const message = reply.trim();
    if (!message || !thread?.conversation?.id || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/agent-earn/team-chat/${thread.conversation.id}/messages`, { message });
      setReply('');
      await openThread(thread.conversation);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    await api.patch(`/api/admin/agent-earn/team-chat/${thread.conversation.id}/status`, { status: 'resolved' });
    await openThread(thread.conversation);
    await load();
  };

  if (thread) {
    return (
      <section className="hub-panel">
        <div className="hub-chat-thread" style={{ border: 0, padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <b style={{ fontSize: 13 }}>{thread.conversation.team_label} — {thread.conversation.subject}</b>
              <small style={{ display: 'block', color: 'var(--hub-muted)', fontSize: 11 }}>
                {thread.conversation.agent?.name || thread.conversation.agent?.username || 'Agent'}
              </small>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <StatusPill value={thread.conversation.status} />
              {thread.conversation.status !== 'resolved' && (
                <button type="button" className="hub-button is-ghost" onClick={resolve}>Resolve</button>
              )}
              <button type="button" className="hub-icon-button" onClick={() => setThread(null)} aria-label="Back to all conversations"><X /></button>
            </div>
          </div>
          {(thread.messages || []).map((msg) => (
            <div key={msg.id} className={`hub-msg ${msg.sender_role === 'agent' ? 'from-agent' : 'from-admin'}`}>
              {msg.body}
              <small>{msg.sender_real_name || msg.sender_name} · {timeAgo(msg.created_at)}</small>
            </div>
          ))}
          <div className="hub-chat-reply">
            <input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
              placeholder="Reply as the team…"
              aria-label="Reply message"
            />
            <button type="button" className="hub-button" onClick={sendReply} disabled={busy || !reply.trim()}><Send /></button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="hub-panel">
      <div className="hub-chat-list">
        {conversations === null ? <div className="hub-empty">Loading conversations…</div>
          : conversations.length === 0 ? <div className="hub-empty">No team requests yet.</div>
            : conversations.map((conversation) => (
              <button key={conversation.id} type="button" className="hub-chat-item" onClick={() => openThread(conversation)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <b>{conversation.team_label} — {conversation.subject}</b>
                  <StatusPill value={conversation.status} />
                </div>
                <small>
                  {(conversation.agent?.name || conversation.agent?.username || 'Agent')} · {conversation.last_message} · {timeAgo(conversation.updated_at)}
                </small>
              </button>
            ))}
      </div>
    </section>
  );
}

function ProfitSharingSection({ tenants, onConfigure }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId && tenants[0]?.id) setTenantId(tenants[0].id);
  }, [tenantId, tenants]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/api/admin/agent-earn/profit-sharing?tenant_id=${encodeURIComponent(tenantId)}`)
      .then((res) => { if (!cancelled) setReport(res.data || {}); })
      .catch((err) => { if (!cancelled) { setReport(null); setError(describeError(err)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId]);

  const rows = report?.rows || [];
  const totals = report?.totals || {};
  const share = Number(report?.platform_support_share_percent || 0);

  return (
    <>
      <section className="hub-panel">
        <div className="hub-panel-head">
          <div>
            <h2>Post-fee Profit Sharing</h2>
            <p>Transparent allocation after every configured fee. Agent earnings and their existing payout ledger are unchanged.</p>
          </div>
          <button type="button" className="hub-button" onClick={onConfigure}><SlidersHorizontal /> Set support share</button>
        </div>
        {tenants.length > 1 && (
          <label className="hub-form" style={{ maxWidth: 320, marginBottom: 16 }}>
            <span>Tenant</span>
            <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name || tenant.slug}</option>)}
            </select>
          </label>
        )}
        <div className="hub-kpis" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-text)' }}><small>Post-fee profit</small><strong className="hub-num">{money(totals.base_profit_usd)}</strong><span>Across assigned agents</span></div>
          <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-earn)' }}><small>Agents</small><strong className="hub-num">{money(totals.agent_share_usd)}</strong><span>Each exact assigned %</span></div>
          <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-in)' }}><small>Platform Support Team</small><strong className="hub-num">{money(totals.platform_support_share_usd)}</strong><span>{share}% tenant rule</span></div>
          <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-border-bright)' }}><small>Company</small><strong className="hub-num">{money(totals.company_share_usd)}</strong><span>Automatic remainder</span></div>
        </div>
      </section>

      <section className="hub-panel">
        <div className="hub-panel-head">
          <div><h2>Agent allocation</h2><p>Every row totals 100% of that agent’s post-fee profit.</p></div>
        </div>
        {loading ? <div className="hub-empty">Calculating post-fee allocations…</div>
          : error ? <div className="hub-empty">{error}</div>
            : rows.length === 0 ? <div className="hub-empty">No agents are linked to this tenant yet.</div>
              : <div className="hub-table-wrap"><table className="hub-table"><thead><tr><th>Agent</th><th>Post-fee profit</th><th>Agent</th><th>Platform Support Team</th><th>Company</th><th>Total</th></tr></thead><tbody>
                {rows.map((row) => <tr key={row.agent_id}>
                  <td><div className="hub-agent-cell"><span className="hub-avatar">{String(row.agent_name || '?').slice(0, 1).toUpperCase()}</span><div><b>{row.agent_name || 'Agent'}</b><small>{row.assignment_active ? 'Active link' : 'Paused link'}{row.disabled ? ' · Disabled' : ''}</small></div></div></td>
                  <td className="hub-num">{money(row.base_profit_usd)}</td>
                  <td className="hub-cell-earn hub-num">{Number(row.agent_share_percent || 0)}% · {money(row.agent_share_usd)}</td>
                  <td className="hub-cell-in hub-num">{Number(row.platform_support_share_percent || 0)}% · {money(row.platform_support_share_usd)}</td>
                  <td className="hub-num">{Number(row.company_share_percent || 0)}% · {money(row.company_share_usd)}</td>
                  <td className="hub-cell-muted hub-num">{Number(row.total_share_percent || 0)}% · {money(row.total_share_usd)}</td>
                </tr>)}
              </tbody></table></div>}
      </section>
    </>
  );
}

function RulesSection({ tenants }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id || '');
  const [form, setForm] = useState(null);
  const [games, setGames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState('');

  useEffect(() => { if (!tenantId && tenants[0]?.id) setTenantId(tenants[0].id); }, [tenants, tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      const res = await api.get(`/api/admin/agent-earn/profit-rules?tenant_id=${tenantId}`);
      if (cancelled) return;
      const source = res.data?.tenant_rule || res.data?.effective || {};
      setForm({
        commission_percent: String(source.commission_percent ?? 10),
        payment_fee_percent: String(source.payment_fee_percent ?? 0),
        cashout_fee_percent: String(source.cashout_fee_percent ?? 0),
        web_fee_percent: String(source.web_fee_percent ?? 0),
        net_house_fee_percent: String(source.net_house_fee_percent ?? 0),
        platform_support_share_percent: String(source.platform_support_share_percent ?? 0),
        game_net_house_rates: { ...(source.game_net_house_rates || {}) },
      });
      setGames(res.data?.games || []);
    })().catch(() => { setForm(null); setGames([]); });
    return () => { cancelled = true; };
  }, [tenantId]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await api.put('/api/admin/agent-earn/profit-rules', {
        tenant_id: tenantId,
        agent_id: null,
        commission_percent: Number(form.commission_percent || 0),
        payment_fee_percent: Number(form.payment_fee_percent || 0),
        cashout_fee_percent: Number(form.cashout_fee_percent || 0),
        web_fee_percent: Number(form.web_fee_percent || 0),
        net_house_fee_percent: Number(form.net_house_fee_percent || 0),
        platform_support_share_percent: Number(form.platform_support_share_percent || 0),
        game_point_rates: {},
        game_net_house_rates: Object.fromEntries(
          Object.entries(form.game_net_house_rates || {})
            .filter(([, value]) => value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => [key, Number(value || 0)])
        ),
      });
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="hub-panel" style={{ maxWidth: 560 }}>
      <div className="hub-panel-head">
        <div>
          <h2>Tenant default rules</h2>
          <p>Every agent without a personal override runs on these numbers, and new signups inherit them automatically.</p>
        </div>
      </div>
      <div className="hub-form">
        {tenants.length > 1 && (
          <label>
            <span>Tenant</span>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name || tenant.slug}</option>)}
            </select>
          </label>
        )}
        {!form ? <div className="hub-empty">Loading rules…</div> : (
          <>
            <div className="hub-form-row">
              <label><span>IN fee % (deposits)</span><input type="number" min="0" max="100" step="0.1" value={form.payment_fee_percent} onChange={set('payment_fee_percent')} /></label>
              <label><span>OUT fee % (cashouts)</span><input type="number" min="0" max="100" step="0.1" value={form.cashout_fee_percent} onChange={set('cashout_fee_percent')} /></label>
            </div>
            <div className="hub-form-row">
              <label><span>Web fee % (deposits)</span><input type="number" min="0" max="100" step="0.1" value={form.web_fee_percent} onChange={set('web_fee_percent')} /></label>
              <label><span>Net house % (points)</span><input type="number" min="0" max="100" step="0.1" value={form.net_house_fee_percent} onChange={set('net_house_fee_percent')} /></label>
            </div>
            <label><span>Agent commission %</span><input type="number" min="0" max="100" step="0.1" value={form.commission_percent} onChange={set('commission_percent')} /></label>
            <label><span>Platform Support Team % (after fees)</span><input type="number" min="0" max="100" step="0.1" value={form.platform_support_share_percent} onChange={set('platform_support_share_percent')} /></label>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--hub-muted)' }}>The agent continues to receive their exact assigned commission. Support is shown after fees, and Company automatically receives the remaining share to 100%.</p>
            <div className="hub-stat" style={{ display: 'grid', gap: 8 }}>
              <div>
                <b style={{ fontSize: 12.5 }}>Per-game game-points rates</b>
                <small style={{ display: 'block', marginTop: 3, color: 'var(--hub-muted)', fontSize: 11 }}>Set the live net-house percentage for each working game. Leave a game blank to use the tenant default. Example: net house $100 × 12% = $12.</small>
              </div>
              {games.length === 0 ? <small style={{ color: 'var(--hub-muted)' }}>No working games are configured for this tenant yet.</small>
                : <div style={{ display: 'grid', gap: 7, maxHeight: 300, overflowY: 'auto', paddingRight: 2 }}>
                  {games.map((game) => (
                    <label key={game.id} className="hub-form-row" style={{ gridTemplateColumns: 'minmax(0, 1fr) 108px', alignItems: 'center' }}>
                      <span style={{ minWidth: 0 }}><b style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name || game.id}</b><small style={{ color: 'var(--hub-faint)' }}>{game.id}</small></span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        inputMode="decimal"
                        placeholder={`${form.net_house_fee_percent || 0}% default`}
                        value={form.game_net_house_rates?.[game.id] ?? ''}
                        onChange={(e) => setForm((prev) => {
                          const next = { ...(prev.game_net_house_rates || {}) };
                          if (e.target.value === '') delete next[game.id];
                          else next[game.id] = e.target.value;
                          return { ...prev, game_net_house_rates: next };
                        })}
                        aria-label={`${game.name || game.id} game-points rate percentage`}
                      />
                    </label>
                  ))}
                </div>}
            </div>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--hub-muted)' }}>
              Saving switches every agent without a personal override to the profit basis with these fees and game rates — the same instant, everywhere: their portal, this hub, and the money itself. Profit Sharing is reporting only; it never changes an agent wallet or payout ledger.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
              {saveError && <small style={{ color: 'var(--hub-out)', fontSize: 11 }}>{saveError}</small>}
              {savedAt && <small style={{ color: 'var(--hub-ok)', fontSize: 11 }}>Saved ✓</small>}
              <button type="button" className="hub-button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save rules'}</button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function AgentsHubPage() {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | login | ready
  const [range, setRange] = useState(30);
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [section, setSection] = useState('dashboard');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/agent-earn/hub');
      setData(res.data);
      setPhase('ready');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) setPhase('login');
      else setPhase('ready');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const logout = () => {
    localStorage.removeItem(HUB_TOKEN_KEY);
    setData(null);
    setPhase('login');
  };

  const totals = data?.totals || {};
  const agents = useMemo(() => {
    const list = data?.agents || [];
    const term = query.trim().toLowerCase();
    if (!term) return list;
    return list.filter((agent) => [agent.name, agent.username, agent.email, agent.tracking_code]
      .some((value) => String(value || '').toLowerCase().includes(term)));
  }, [data, query]);

  const chartData = useMemo(() => (data?.daily || []).slice(-range).map((row) => ({
    ...row,
    label: dayLabel(row.day),
  })), [data, range]);

  const topAgents = useMemo(() => {
    const earners = (data?.agents || []).filter((agent) => agent.earned_usd > 0).slice(0, 5);
    const max = Math.max(...earners.map((agent) => agent.earned_usd), 1);
    return earners.map((agent) => ({ ...agent, share: Math.round((agent.earned_usd / max) * 100) }));
  }, [data]);

  if (phase === 'login') return <div className="agents-hub"><HubLogin onDone={load} /></div>;

  const SECTIONS = {
    dashboard: { title: 'Dashboard', desc: 'The whole roster at a glance.', asset: HUB_SECTION_ASSETS.dashboard },
    leads: { title: 'Lead Center', desc: 'SO Agent survey leads, assignment, and follow-up.', asset: HUB_SECTION_ASSETS.leads },
    agents: { title: 'Agents', desc: 'Every agent — click one for the full story.', asset: HUB_SECTION_ASSETS.agents },
    ledger: { title: 'Ledger', desc: 'Every commission event across the roster, filterable and exportable.', asset: HUB_SECTION_ASSETS.ledger },
    statements: { title: 'Statements', desc: 'What each agent is owed for a month, straight from the ledger.', asset: HUB_SECTION_ASSETS.statements },
    withdrawals: { title: 'Withdrawals', desc: 'Agent payout requests, ready to decide.', asset: HUB_SECTION_ASSETS.withdrawals },
    'profit-sharing': { title: 'Profit Sharing', desc: 'Post-fee agent, support-team, and company allocation.', asset: HUB_SECTION_ASSETS['profit-sharing'] },
    referrals: { title: 'Referral Network', desc: 'Who recruited whom, and what the overrides paid.', asset: HUB_SECTION_ASSETS.referrals },
    chat: { title: 'Team Chat', desc: 'Every request agents have sent to the teams.', asset: HUB_SECTION_ASSETS.chat },
    oversight: { title: 'Chat Oversight', desc: 'Customer chat queues — who is keeping up.', asset: HUB_SECTION_ASSETS.oversight },
    broadcast: { title: 'Broadcast', desc: 'One message into every agent’s team inbox.', asset: HUB_SECTION_ASSETS.broadcast },
    domains: { title: 'Domains', desc: 'Where agents sign in, and whether that door is open.', asset: HUB_SECTION_ASSETS.domains },
    audit: { title: 'Audit Log', desc: 'Who changed what, and when.', asset: HUB_SECTION_ASSETS.audit },
    settings: { title: 'Settings', desc: 'Tenant default fees and commission.', asset: HUB_SECTION_ASSETS.settings },
  };
  const NAV_GROUPS = [
    { label: 'Operate', sections: ['dashboard', 'leads', 'agents', 'chat', 'oversight'] },
    { label: 'Money', sections: ['ledger', 'statements', 'withdrawals', 'profit-sharing'] },
    { label: 'Network', sections: ['referrals', 'broadcast', 'domains'] },
    { label: 'Control', sections: ['audit', 'settings'] },
  ];
  const active = SECTIONS[section];

  return (
    <div className="agents-hub">
      <div className="hub-shell">
        <aside className={`hub-sidebar ${mobileNavOpen ? 'is-mobile-open' : ''}`}>
          <div className="hub-brand">
            <span className="hub-brand-mark"><img src={hubRelayMark} alt="" /></span>
            <div>
              <h1>AgentHub</h1>
              <p>{data?.tenant?.name || 'Global control plane'}</p>
            </div>
          </div>
          <div className="hub-scope-card">
            <Building2 />
            <span><small>Active scope</small><b>{data?.tenant?.name || 'All networks'}</b></span>
            <i aria-hidden="true" />
          </div>
          <nav className="hub-nav" aria-label="Hub sections">
            {NAV_GROUPS.map((group) => (
              <div className="hub-nav-group" key={group.label}>
                <span className="hub-nav-group-label">{group.label}</span>
                {group.sections.map((id) => {
                  const meta = SECTIONS[id];
                  const badge = id === 'withdrawals' ? totals.pending_withdrawals : id === 'chat' ? totals.open_team_chats : 0;
                  return (
                    <button key={id} type="button" title={meta.title} className={section === id ? 'is-active' : ''} onClick={() => { setSection(id); setMobileNavOpen(false); }}>
                      <span className="hub-nav-icon"><HubAssetIcon src={meta.asset} /></span><span>{meta.title}</span>
                      {badge > 0 && <i className="hub-nav-badge hub-num">{badge}</i>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="hub-sidebar-foot">
            <nav className="hub-nav">
              <button type="button" onClick={refresh}>
                <RefreshCw style={refreshing ? { animation: 'spin 0.9s linear infinite' } : undefined} /> <span>Refresh</span>
              </button>
              <button type="button" onClick={() => { setPasswordOpen(true); setMobileNavOpen(false); }}><KeyRound /> <span>Change password</span></button>
              <button type="button" onClick={logout}><LogOut /> <span>Sign out</span></button>
            </nav>
          </div>
        </aside>

        {mobileNavOpen && (
          <button type="button" className="hub-mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" />
        )}

        <div className="hub-content">
          <div className="hub-content-head">
            <div className="hub-content-title">
              <button type="button" className="hub-mobile-menu" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation"><Menu /></button>
              <span className="hub-section-icon"><HubAssetIcon src={active.asset} /></span>
              <div>
                <h1>{active.title}</h1>
                <p>{active.desc}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="hub-chip"><Activity style={{ width: 12, height: 12 }} /> <b className="hub-num">{totals.agents || 0}</b> agents</span>
              {section === 'agents' && (
                <button type="button" className="hub-button" onClick={() => setCreating(true)}><UserPlus /> New agent</button>
              )}
            </div>
          </div>

          {phase === 'loading' ? (
            <div className="hub-loading"><img src={hubRelayMark} alt="" /><span>Synchronizing AgentHub…</span></div>
          ) : (
            <main className="hub-content-main">
              {section === 'dashboard' && (
                <>
          <section className="hub-command-band" aria-label="AgentHub overview">
            <div className="hub-command-copy">
              <span className="hub-eyebrow">Global agent operations</span>
              <h2>One desk for every<br />agent network.</h2>
              <p>Follow the roster, resolve conversations, and trace every earning decision from one calm control surface.</p>
              <div className="hub-command-tags">
                <span><i className="is-live" /> Live roster</span>
                <span>Tenant scoped</span>
                <span>Ledger first</span>
              </div>
            </div>
            <div className="hub-command-summary" aria-label="Live summary">
              <span><small>Agents online</small><b className="hub-num">{totals.agents || 0}</b></span>
              <span><small>Open decisions</small><b className="hub-num">{Number(totals.pending_withdrawals || 0) + Number(totals.open_team_chats || 0)}</b></span>
            </div>
            <img className="hub-command-art" src={hubCommandNetwork} alt="" />
          </section>
          <section className="hub-attention" aria-label="Needs attention">
            {(() => {
              const weekAgo = Date.now() - 7 * 86400000;
              const noScheme = (data?.agents || []).filter((agent) => !agent.has_assignment && !agent.disabled);
              const fresh = (data?.agents || []).filter((agent) => agent.joined_at && new Date(agent.joined_at).getTime() > weekAgo);
              const items = [
                totals.pending_withdrawals > 0 && { key: 'wd', to: 'withdrawals', label: <><b>{totals.pending_withdrawals}</b> withdrawal request{totals.pending_withdrawals === 1 ? '' : 's'} waiting</>, hint: 'Approve or reject' },
                totals.open_team_chats > 0 && { key: 'chat', to: 'chat', label: <><b>{totals.open_team_chats}</b> open team request{totals.open_team_chats === 1 ? '' : 's'}</>, hint: 'Reply or resolve' },
                noScheme.length > 0 && { key: 'scheme', to: 'agents', label: <><b>{noScheme.length}</b> agent{noScheme.length === 1 ? '' : 's'} earning nothing — no scheme</>, hint: 'Open and save settings once' },
                fresh.length > 0 && { key: 'new', to: 'agents', label: <><b>{fresh.length}</b> new agent{fresh.length === 1 ? '' : 's'} this week</>, hint: 'Say hello, check their link' },
              ].filter(Boolean);
              if (!items.length) return <div className="hub-attention-clear">All clear — nothing needs a decision right now.</div>;
              return items.map((item) => (
                <button key={item.key} type="button" className="hub-attention-item" onClick={() => setSection(item.to)}>
                  <span style={{ color: 'var(--hub-text)' }}>{item.label}</span>
                  <span>{item.hint} →</span>
                </button>
              ));
            })()}
          </section>
          <section className="hub-kpis" aria-label="Roster totals">
            <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-earn)' }}>
              <small>Agents</small>
              <strong className="hub-num">{totals.agents || 0}</strong>
              <span>{totals.agents_with_assignment || 0} on a scheme · {totals.agents_disabled || 0} disabled</span>
            </div>
            <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-in)' }}>
              <small>Money IN</small>
              <strong className="hub-num">{compact(totals.in_usd)}</strong>
              <span>{totals.customers || 0} attributed customers</span>
            </div>
            <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-out)' }}>
              <small>Money OUT</small>
              <strong className="hub-num">{compact(totals.out_usd)}</strong>
              <span>Customer cashouts</span>
            </div>
            <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-border-bright)' }}>
              <small>Net profit</small>
              <strong className="hub-num">{compact(totals.net_profit_usd)}</strong>
              <span>{compact(totals.fees_usd)} in fees</span>
            </div>
            <div className="hub-kpi" style={{ '--kpi-accent': 'var(--hub-earn)' }}>
              <small>Agents earned</small>
              <strong className="hub-num">{compact(totals.earned_usd)}</strong>
              <span>{compact(totals.locked_usd)} locked · {compact(totals.withdrawn_usd)} paid out</span>
            </div>
          </section>

          <div className="hub-grid">
            <section className="hub-panel" aria-label="Flow across all agents">
              <div className="hub-panel-head">
                <div>
                  <h2>Roster flow</h2>
                  <p>Deposits in, cashouts out, and agent earnings — every agent summed, per day.</p>
                </div>
                <div className="hub-range">
                  {[7, 30, 90].map((value) => (
                    <button key={value} type="button" className={range === value ? 'is-active' : ''} onClick={() => setRange(value)}>{value}d</button>
                  ))}
                </div>
              </div>
              <div className="hub-legend">
                <span><i style={{ background: 'var(--hub-in)' }} />IN</span>
                <span><i style={{ background: 'var(--hub-out)' }} />OUT</span>
                <span><i style={{ background: 'var(--hub-earn)' }} />Agents earned</span>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                  <defs>
                    <linearGradient id="hubIn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#275dff" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#275dff" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#e2ddd4" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#697586', fontSize: 11 }} interval="preserveStartEnd" minTickGap={26} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: '#697586', fontSize: 11 }} tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
                  <Tooltip content={<HubTooltip />} />
                  <Area type="monotone" dataKey="in_usd" name="IN" stroke="#275dff" strokeWidth={2} fill="url(#hubIn)" />
                  <Bar dataKey="out_usd" name="OUT" fill="#ff5c3a" radius={[3, 3, 0, 0]} maxBarSize={14} opacity={0.88} />
                  <Line type="monotone" dataKey="earned_usd" name="Earned" stroke="#6f9f00" strokeWidth={2.2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </section>

            <section className="hub-panel" aria-label="Top agents">
              <div className="hub-panel-head">
                <div>
                  <h2>Top agents</h2>
                  <p>Ranked by lifetime earnings.</p>
                </div>
              </div>
              {topAgents.length === 0 ? (
                <div className="hub-empty">No agent has earned yet.</div>
              ) : (
                <div className="hub-lead">
                  {topAgents.map((agent, index) => (
                    <div key={agent.id} className="hub-lead-row">
                      <span className="hub-lead-rank hub-num">{index + 1}</span>
                      <div className="hub-lead-info">
                        <b>{agent.name}</b>
                        <div className="hub-lead-bar"><i style={{ width: `${agent.share}%` }} /></div>
                      </div>
                      <div>
                        <span className="hub-lead-val hub-num">{money(agent.earned_usd)}</span>
                        <span className="hub-lead-sub hub-num">{agent.customers} customers</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
                </>
              )}

              {section === 'agents' && (
          <section className="hub-panel" aria-label="Full roster">
            <div className="hub-panel-head">
              <div>
                <h2>Full roster</h2>
                <p>Every agent, every flow — the same math each agent sees in their portal.</p>
              </div>
              <input className="hub-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search agents…" aria-label="Search agents" />
            </div>
            <div className="hub-table-wrap">
              <table className="hub-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Status</th>
                    <th>Rate</th>
                    <th>Customers</th>
                    <th>IN</th>
                    <th>OUT</th>
                    <th>Points</th>
                    <th>Fees</th>
                    <th>Net</th>
                    <th>Earned</th>
                    <th>Available</th>
                    <th>Locked</th>
                    <th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length === 0 ? (
                    <tr><td colSpan={13}><div className="hub-empty">No agents match.</div></td></tr>
                  ) : agents.map((agent) => {
                    const status = agentStatus(agent);
                    return (
                      <tr key={agent.id} onClick={() => setSelectedAgentId(agent.id)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="hub-agent-cell">
                            <span className="hub-avatar">{String(agent.name || '?').slice(0, 1).toUpperCase()}</span>
                            <div>
                              <b>{agent.name}</b>
                              <small>{agent.tracking_code || agent.email}</small>
                            </div>
                          </div>
                        </td>
                        <td><span className={`hub-status ${status.cls}`}><i />{status.label}</span></td>
                        <td className="hub-cell-muted hub-num">{agent.commission_percent}%{agent.commission_basis ? ` ${agent.commission_basis === 'profit' ? 'profit' : 'rev'}` : ''}</td>
                        <td className="hub-num">{agent.customers}</td>
                        <td className="hub-cell-in hub-num">{money(agent.in_usd)}</td>
                        <td className="hub-cell-out hub-num">{money(agent.out_usd)}</td>
                        <td className="hub-cell-muted hub-num">{Number(agent.points_used || 0).toLocaleString()}</td>
                        <td className="hub-cell-muted hub-num">{money(agent.fees_usd)}</td>
                        <td className="hub-num">{money(agent.net_profit_usd)}</td>
                        <td className="hub-cell-earn hub-num">{money(agent.earned_usd)}</td>
                        <td className="hub-num">{money(agent.available_usd)}</td>
                        <td className="hub-cell-muted hub-num">{money(agent.locked_usd)}</td>
                        <td className="hub-cell-muted">{timeAgo(agent.last_activity_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="hub-mobile-agent-list" aria-label="Agent roster">
              {agents.length === 0 ? <div className="hub-empty">No agents match.</div> : agents.map((agent) => {
                const status = agentStatus(agent);
                return (
                  <button key={agent.id} type="button" className="hub-mobile-agent-card" onClick={() => setSelectedAgentId(agent.id)}>
                    <div className="hub-mobile-agent-card-head">
                      <div className="hub-agent-cell">
                        <span className="hub-avatar">{String(agent.name || '?').slice(0, 1).toUpperCase()}</span>
                        <div><b>{agent.name}</b><small>{agent.tracking_code || agent.username || 'No referral code'}</small></div>
                      </div>
                      <span className={`hub-status ${status.cls}`}><i />{status.label}</span>
                    </div>
                    <div className="hub-mobile-agent-metrics">
                      <span><small>Rate</small><b>{agent.commission_percent}%</b></span>
                      <span><small>Customers</small><b className="hub-num">{agent.customers}</b></span>
                      <span><small>Earned</small><b className="hub-cell-earn hub-num">{money(agent.earned_usd)}</b></span>
                      <span><small>Available</small><b className="hub-num">{money(agent.available_usd)}</b></span>
                    </div>
                    <span className="hub-mobile-agent-activity">Last activity {timeAgo(agent.last_activity_at)} · Open details</span>
                  </button>
                );
              })}
            </div>
          </section>
              )}

              {section === 'leads' && <LeadCenterSection agents={data?.agents || []} />}
              {section === 'ledger' && <LedgerSection />}
              {section === 'statements' && <StatementsSection />}
              {section === 'withdrawals' && <WithdrawalsSection />}
              {section === 'profit-sharing' && <ProfitSharingSection tenants={data?.tenants || []} onConfigure={() => setSection('settings')} />}
              {section === 'referrals' && <ReferralNetworkSection />}
              {section === 'chat' && <TeamChatSection />}
              {section === 'oversight' && <ChatOversightSection />}
              {section === 'broadcast' && <BroadcastSection agentCount={totals.agents || 0} onSent={load} />}
              {section === 'domains' && <DomainsSection />}
              {section === 'audit' && <AuditSection />}
              {section === 'settings' && <RulesSection tenants={data?.tenants || []} />}
            </main>
          )}
        </div>
      </div>

      {passwordOpen && <ChangePasswordModal onClose={() => setPasswordOpen(false)} />}

      {selectedAgentId && (
        <AgentDrawer
          agentId={selectedAgentId}
          onClose={() => setSelectedAgentId(null)}
          onChanged={load}
        />
      )}
      {creating && (
        <NewAgentModal
          tenants={data?.tenants || []}
          onClose={() => setCreating(false)}
          onCreated={async (agentId) => {
            setCreating(false);
            await load();
            if (agentId) setSelectedAgentId(agentId);
          }}
        />
      )}
    </div>
  );
}
